/* eslint-disable camelcase */
import { useCallback, useEffect, useRef, useState } from 'react'
import dayjs from 'dayjs'

/* Utils */
import axios from 'lib/axios'
import {
  AcaComparisonPayload,
  AcaComparisonPayloadSnowflake,
  AcaCoverageFilter,
  AcaLastSeen,
  AcaOperatingSystem,
  ALL_SOURCES_SENTINEL,
  AnalysisData,
  AnalyzerData,
  CompareToAllPopulations,
  CPITimePeriod,
  IntegrationInstance,
  LastSeenWithin,
  ManifestIntegration,
  OperatingSystem,
  TotalComparableAssetsData
} from 'models'
import { downloadFile, getCompareToAllCoverage, getDownloadCsvProps, DEFAULT_TRENDS_TIME_PERIOD } from './utils'

export interface Pagination {
  current_page: number
  next_page: number | null
  prev_page: number | null
  total_pages: number
  total_records: number
}

const useCoverageAnalyzerData = () => {
  const [firstSource, setFirstSource] = useState('')
  const [secondSource, setSecondSource] = useState('')
  const [selectedOperatingSystem, setSelectedOperatingSystem] = useState<OperatingSystem>(OperatingSystem.All)
  const [selectedOsTrends, setSelectedOsTrends] = useState<OperatingSystem>(OperatingSystem.All)
  const [selectedSeenWithin, setSelectedSeenWithin] = useState<LastSeenWithin>(LastSeenWithin.All)
  const [activeSeenWithin, setActiveSeenWithin] = useState<LastSeenWithin>(LastSeenWithin.All)
  const [lastActiveSeenWithin, setLastActiveSeenWithin] = useState<LastSeenWithin>(LastSeenWithin.All)
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<CPITimePeriod>(DEFAULT_TRENDS_TIME_PERIOD)
  const [chartData, setChartData] = useState<AnalyzerData | null>(null)
  const [chartLoading, setChartLoading] = useState(false)
  const [optionsLoading, setOptionsLoading] = useState(true)
  const [loadingAnalysisData, setLoadingAnalysisData] = useState(true)
  const [instances, setInstances] = useState<IntegrationInstance[]>([])
  const [supportedIntegrations, setSupportedIntegrations] = useState<ManifestIntegration[]>([])
  const [allAnalysisData, setAllAnalysisData] = useState<AnalysisData[]>([])
  const [analysisData, setAnalysisData] = useState<AnalysisData[]>([])
  const [selectedInstances, setSelectedInstances] = useState<IntegrationInstance[]>([])
  const [pagination, setPagination] = useState<Pagination>({
    current_page: 1,
    next_page: null,
    prev_page: null,
    total_pages: 1,
    total_records: 0
  })
  const [tableFilters, setTableFilters] = useState([false, false, false, false])
  const [firstInstanceAssets, setFirstInstanceAssets] = useState(0)
  const [secondInstanceAssets, setSecondInstanceAssets] = useState(0)
  const [numOfActiveAssets, setNumOfActiveAssets] = useState(0)
  const [sourceChanged, setSourceChanged] = useState(true)
  const [initialLoad, setInitialLoad] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [totalComparableAssets, setTotalComparableAssets] = useState<TotalComparableAssetsData | null>(null)

  /* Compare-to-All: true when one source slot holds the "All" sentinel. The picked product is the
   * other slot. A ref keeps the latest source values readable inside fetchAnalysisData without
   * widening its useCallback deps (re-query call sites pass only the picked id). */
  const isCompareToAll = firstSource === ALL_SOURCES_SENTINEL || secondSource === ALL_SOURCES_SENTINEL
  const sourcesRef = useRef({ firstSource, secondSource })
  sourcesRef.current = { firstSource, secondSource }

  /* Committed Compare-to-All flag: reflects the mode of the data currently in the table (set when a
   * fetch populates it), NOT the live dropdown. Rendering (table mapping, logo, row panel) keys off
   * this so picking "All" without clicking Analyze leaves the existing table untouched. */
  const [analyzedCompareToAll, setAnalyzedCompareToAll] = useState(false)

  const [analyzedTrendMode, setAnalyzedTrendMode] = useState({ isCompareToAll: false, sentinelInSourceA: false })

  const handleChangeSource = (source: 1 | 2, value: string) => {
    switch (source) {
      case 1:
        setFirstSource(value)
        break
      case 2:
        setSecondSource(value)
        break
    }

    let sourceChanged = true

    if (selectedInstances.length > 1) {
      const first = source === 1 ? value : firstSource
      const second = source === 2 ? value : secondSource
      if (first === selectedInstances[0].id && second === selectedInstances[1].id) {
        sourceChanged = false
      }
    }

    if (sourceChanged) {
      setSourceChanged(sourceChanged)
    } else {
      setSourceChanged(selectedSeenWithin !== lastActiveSeenWithin)
    }
  }

  const fetchInstances = async () => {
    const supportedRes = await axios.get('/api/v3/integrations/supported')

    const acaSupportedIntegrationNames: string[] = []
    let manifestIntegrations: ManifestIntegration[] = []
    if (supportedRes.status === 200) {
      const integrations: ManifestIntegration[] = supportedRes.data.integrations.filter(
        (n: ManifestIntegration) => n.is_supports_aca
      )
      integrations.forEach((integration) => acaSupportedIntegrationNames.push(integration.name))
      manifestIntegrations = integrations
      setSupportedIntegrations(integrations)
    }

    const res = await axios.get('/api/v3/integrations/configured')

    if (res.status === 200) {
      let instances: IntegrationInstance[] = res.data.instances
      if (acaSupportedIntegrationNames.length > 0) {
        instances = instances.filter((n) => acaSupportedIntegrationNames.includes(n.integration_name))
      }
      /* Join the manifest's authoritative logo_path onto each instance so the table and drawer render
       * the same logo the data-source dropdown does (matched on integration_name === manifest.name). */
      instances = instances.map((instance) => ({
        ...instance,
        logo_path:
          manifestIntegrations.find((m) => m.name === instance.integration_name)?.logo_path ?? instance.logo_path
      }))
      setInstances(instances)
    }
  }

  const fetchAll = async () => {
    setOptionsLoading(true)

    try {
      await fetchInstances()
    } catch (e) {
      console.error(e)
    } finally {
      setOptionsLoading(false)
    }
  }

  const fetchTrendData = async ({
    timePeriod = selectedTimePeriod,
    seenWithin = selectedSeenWithin,
    operatingSystem = selectedOperatingSystem
  }: {
    timePeriod?: CPITimePeriod
    seenWithin?: LastSeenWithin
    operatingSystem?: OperatingSystem
  }) => {
    setChartLoading(true)

    const integrationIds: string[] = []
    if (firstSource) {
      integrationIds.push(firstSource)
    }
    if (secondSource) {
      integrationIds.push(secondSource)
    }

    const instance1 = instances.find((n) => n.id === integrationIds[0])
    const instance2 = instances.find((n) => n.id === integrationIds[1])
    const isV2 = Boolean(instance1?.is_v2 && instance2?.is_v2)

    const trendCompareToAll = firstSource === ALL_SOURCES_SENTINEL || secondSource === ALL_SOURCES_SENTINEL
    const trendSentinelInSourceA = firstSource === ALL_SOURCES_SENTINEL

    const operating_system = operatingSystem.toLowerCase()
    const last_seen = seenWithin.toLowerCase()

    try {
      let res

      if (trendCompareToAll) {
        /* Compare-to-All trend: two series (picked product + "All others" union) from the dedicated
         * trend endpoint. Body is query_filter-wrapped with the sentinel; no pagination. See
         * docs/api-spec2.md. */
        const pickedInstance = instances.find((n) => n.id === integrationIds.find((id) => id !== ALL_SOURCES_SENTINEL))
        res = await axios.post('/api/v4/asset_coverage/compare-to-all/trend/', {
          query_filter: {
            last_seen,
            operating_system,
            product_instances: [pickedInstance?.instance_name || '', ALL_SOURCES_SENTINEL],
            time_period: timePeriod.toLowerCase()
          }
        })
      } else if (isV2) {
        // Both v2: use the v2 trend endpoint. Migrated v3/wf -> v4 alongside records and the
        // compare-to-all trend (ONYXIA-7093); the old /api/v3/wf/asset_coverage/trend path now 404s.
        const instanceNames = integrationIds
          .map((id) => instances.find((instance) => instance.id === id)?.instance_name || '')
          .filter((c) => !!c)
        res = await axios.post('/api/v4/asset_coverage/trend', {
          query_filter: {
            last_seen,
            operating_system,
            product_instances: instanceNames,
            time_period: timePeriod.toLowerCase()
          },
          page: 0
        })
      } else {
        // Non-v2 or mixed: use legacy endpoint
        const time_period = timePeriod.toLowerCase() === 'past year' ? '1y' : timePeriod.toLowerCase()
        res = await axios.post('/api/v3/aca/data', {
          time_period,
          operating_system,
          integration_ids: integrationIds,
          last_seen
        })
      }

      if (res.status.toString().includes('20') && res.data) {
        setChartData(res.data.data)
        setAnalyzedTrendMode({ isCompareToAll: trendCompareToAll, sentinelInSourceA: trendSentinelInSourceA })
      }
    } catch (e) {
      console.error(e)
    } finally {
      setChartLoading(false)
    }
  }

  const fetchTotalAssets = async ({
    integrationIds,
    lastSeenWithin
  }: {
    integrationIds: string[]
    lastSeenWithin: AcaLastSeen
  }) => {
    try {
      const payload = {
        integration_ids: integrationIds,
        last_seen: lastSeenWithin
      }
      const res = await axios.post('/api/v3/aca/total_comparable_assets', payload)

      if (res.status.toString().includes('20')) {
        const { data } = res.data
        const firstId = integrationIds[0].slice(0, 8)
        const secondId = integrationIds[1].slice(0, 8)
        for (const [key, value] of Object.entries(data)) {
          if (key.includes(firstId)) {
            setFirstInstanceAssets(value as number)
          }
          if (key.includes(secondId)) {
            setSecondInstanceAssets(value as number)
          }
          if (key === 'total_active') {
            setNumOfActiveAssets(value as number)
          }
        }
      }
    } catch (err) {
      console.error(err)
      setLoadingAnalysisData(false)
    }
  }

  /* Compare-to-All fetch: picks the non-sentinel product and compares it against the union of all
   * other Devices-schema instances via the dedicated clustering endpoint. See docs/aca-api-spec.md. */
  const fetchCompareToAllData = useCallback(
    async ({
      integrationIds,
      operatingSystem = selectedOperatingSystem,
      lastSeenWithin = activeSeenWithin,
      searchTerm = '',
      filters = tableFilters,
      sortBy = 'last_seen',
      direction = 'ASC'
    }: {
      integrationIds: string[]
      operatingSystem?: OperatingSystem
      lastSeenWithin?: LastSeenWithin
      searchTerm?: string
      filters?: boolean[]
      sortBy?: string
      direction?: string
    }) => {
      const pickedId = integrationIds.find((id) => id && id !== ALL_SOURCES_SENTINEL) || ''
      const pickedInstance = instances.find((n) => n.id === pickedId)

      if (!pickedInstance) {
        setLoadingAnalysisData(false)
        return
      }

      setSelectedInstances([pickedInstance])

      const operating_system = operatingSystem.toLowerCase() as AcaOperatingSystem
      const last_seen = lastSeenWithin.toLowerCase() as AcaLastSeen

      const payload: AcaComparisonPayloadSnowflake = {
        query_filter: {
          product_instances: [pickedInstance.instance_name || '', ALL_SOURCES_SENTINEL],
          last_seen,
          operating_system,
          search_term: searchTerm,
          sort_field: sortBy,
          sort_direction: direction
        },
        page_number: 1,
        limit: 20
      }

      /* A/B may be swapped vs the server when the user put "All" in Source A — flip only_a/only_b. */
      const sentinelInSourceA = sourcesRef.current.firstSource === ALL_SOURCES_SENTINEL
      const coverageComparison = getCompareToAllCoverage(filters, sentinelInSourceA)
      if (coverageComparison) {
        payload.query_filter.coverage_comparison = coverageComparison
      }

      try {
        const res = await axios.post('/api/v4/asset_coverage/compare-to-all/records/', payload)

        if (res.status.toString().includes('20')) {
          const { data, pagination: dataPagination, total_comparable_assets } = res.data
          const populations = res.data.populations as CompareToAllPopulations | undefined
          const pagination = { ...dataPagination, total_records: dataPagination.total_results }

          setAnalysisData(data)
          setAllAnalysisData(data)
          setPagination(pagination)
          setAnalyzedCompareToAll(true)

          /* total_comparable_assets still drives the filter-dependent bucket tabs + pagination. */
          if (total_comparable_assets) {
            setTotalComparableAssets(total_comparable_assets)
          }

          /* Constant population totals feed the steady top-bar widgets. `source_a` is always the
           * picked product and `all_others` the deduped rest, so flip them when the user placed
           * "All" in Source A (the A/B columns are swapped vs the server in that case). */
          if (populations) {
            const pickedAssets = populations.source_a ?? 0
            const allOthersAssets = populations.all_others ?? 0
            setFirstInstanceAssets(sentinelInSourceA ? allOthersAssets : pickedAssets)
            setSecondInstanceAssets(sentinelInSourceA ? pickedAssets : allOthersAssets)
            setNumOfActiveAssets(populations.active_assets ?? 0)
          }
        }
      } catch (e) {
        console.error(e)
      } finally {
        setInitialLoad(true)
        setSourceChanged(false)
        setLoadingAnalysisData(false)
      }
    },
    [selectedOperatingSystem, instances, tableFilters, activeSeenWithin]
  )

  const fetchAnalysisData = useCallback(
    async ({
      integrationIds,
      operatingSystem = selectedOperatingSystem,
      lastSeenWithin = activeSeenWithin,
      searchTerm = '',
      filters = tableFilters,
      sortBy = 'last_seen',
      direction = 'ASC'
    }: {
      integrationIds: string[]
      operatingSystem?: OperatingSystem
      lastSeenWithin?: LastSeenWithin
      searchTerm?: string
      filters?: boolean[]
      sortBy?: string
      direction?: string
    }) => {
      setLoadingAnalysisData(true)

      /* Compare-to-All routes to a dedicated clustering endpoint. The mode persists across re-queries
       * (filter/sort/search) via the source fields, even though those call sites only pass the picked
       * id; detect it from the latest source values rather than the integrationIds arg. */
      const { firstSource: fs, secondSource: ss } = sourcesRef.current
      if (fs === ALL_SOURCES_SENTINEL || ss === ALL_SOURCES_SENTINEL) {
        await fetchCompareToAllData({
          integrationIds,
          operatingSystem,
          lastSeenWithin,
          searchTerm,
          filters,
          sortBy,
          direction
        })
        return
      }

      const instance1 = instances.find((n) => n.id === integrationIds[0])
      const instance2 = instances.find((n) => n.id === integrationIds[1])

      if (instance1 && instance2) {
        setSelectedInstances([instance1, instance2])
      }

      const operating_system = operatingSystem.toLowerCase() as AcaOperatingSystem
      const last_seen = lastSeenWithin.toLowerCase() as AcaLastSeen
      const sort_by = sortBy
      const sort_direction = direction
      const search_term = searchTerm

      const payload: AcaComparisonPayload = {
        operating_system,
        integration_ids: integrationIds,
        last_seen,
        page_number: 1,
        sort_by,
        sort_direction,
        search_term
      }

      const instanceNames: string[] = integrationIds
        .map((id) => instances.find((instance) => instance.id === id)?.instance_name || '')
        .filter((c) => !!c)
      const payloadSnowflake: AcaComparisonPayloadSnowflake = {
        query_filter: {
          product_instances: instanceNames,
          last_seen,
          operating_system,
          search_term,
          sort_field: sort_by,
          sort_direction
        },
        page_number: 1,
        limit: 20
      }

      const coverageFilterIndex = filters.findIndex((c) => c)

      if (coverageFilterIndex > -1) {
        const coverage =
          coverageFilterIndex === 0
            ? AcaCoverageFilter.FirstNotSecond
            : coverageFilterIndex === 1
            ? AcaCoverageFilter.SecondNotFirst
            : coverageFilterIndex === 2
            ? AcaCoverageFilter.Intersection
            : AcaCoverageFilter.SymmetricDifference
        payload.coverage = coverage
        payloadSnowflake.query_filter.coverage_comparison = coverage
      } else {
        delete payload.coverage
      }

      try {
        const isV2 = Boolean(instance1?.is_v2 && instance2?.is_v2)
        let res

        if (isV2) {
          res = await axios.post('/api/v4/asset_coverage/records', payloadSnowflake)
        } else {
          await fetchTotalAssets({ integrationIds, lastSeenWithin: lastSeenWithin.toLowerCase() as AcaLastSeen })

          res = await axios.post('/api/v3/aca/comparison', payload)
        }

        if (res.status.toString().includes('20')) {
          const { data, pagination: dataPagination, total_comparable_assets } = res.data
          const pagination = { ...dataPagination }
          if (isV2) {
            pagination.total_records = dataPagination.total_results
          }

          setAnalysisData(data)
          setAllAnalysisData(data)
          setPagination(pagination)
          setSourceChanged(true)
          setAnalyzedCompareToAll(false)

          if (total_comparable_assets) {
            setFirstInstanceAssets(total_comparable_assets[instance1?.instance_name || ''] || 0)
            setSecondInstanceAssets(total_comparable_assets[instance2?.instance_name || ''] || 0)
            setNumOfActiveAssets(total_comparable_assets.active_assets)
            setTotalComparableAssets(total_comparable_assets)
          }
        }
      } catch (e) {
        console.error(e)
      } finally {
        setInitialLoad(true)
        setSourceChanged(false)
        setLoadingAnalysisData(false)
      }
    },
    [selectedOperatingSystem, instances, tableFilters, selectedSeenWithin, fetchCompareToAllData]
  )

  const handleExport = useCallback(
    async ({
      integrationIds = [selectedInstances[0]?.id || '', selectedInstances[1]?.id || ''],
      operatingSystem = selectedOperatingSystem,
      lastSeenWithin = activeSeenWithin,
      searchTerm = '',
      filters = tableFilters,
      sortBy = 'last_seen',
      direction = 'ASC'
    }: {
      integrationIds?: string[]
      operatingSystem?: OperatingSystem
      lastSeenWithin?: LastSeenWithin
      searchTerm?: string
      filters?: boolean[]
      sortBy?: string
      direction?: string
    }) => {
      setExporting(true)

      const operating_system = operatingSystem.toLowerCase() as AcaOperatingSystem
      const last_seen = lastSeenWithin.toLowerCase() as AcaLastSeen
      const sort_by = sortBy
      const sort_direction = direction
      const search_term = searchTerm

      const payload: AcaComparisonPayload = {
        operating_system,
        integration_ids: integrationIds,
        last_seen,
        page_number: 1,
        sort_by,
        sort_direction,
        search_term
      }
      const instanceNames: string[] = integrationIds
        .map((id) => instances.find((instance) => instance.id === id)?.instance_name || '')
        .filter((c) => !!c)
      const snowflakePayload: AcaComparisonPayloadSnowflake = {
        query_filter: {
          product_instances: instanceNames,
          last_seen,
          operating_system,
          search_term,
          sort_field: sort_by,
          sort_direction
        }
      }

      const coverageFilterIndex = filters.findIndex((c) => c)

      if (coverageFilterIndex > -1) {
        const coverage =
          coverageFilterIndex === 0
            ? AcaCoverageFilter.FirstNotSecond
            : coverageFilterIndex === 1
            ? AcaCoverageFilter.SecondNotFirst
            : coverageFilterIndex === 2
            ? AcaCoverageFilter.Intersection
            : AcaCoverageFilter.SymmetricDifference
        payload.coverage = coverage
        snowflakePayload.query_filter.coverage_comparison = coverage
      } else {
        delete payload.coverage
      }

      try {
        const { firstSource: fs, secondSource: ss } = sourcesRef.current
        const compareToAll = fs === ALL_SOURCES_SENTINEL || ss === ALL_SOURCES_SENTINEL

        if (compareToAll) {
          /* Compare-to-All CSV: same query_filter body with the sentinel in product_instances; the
           * export streams the full filtered set (no limit/page_number). See docs/api-spec2.md. */
          const picked = instances.find((n) => n.id === integrationIds.find((id) => id !== ALL_SOURCES_SENTINEL))
          const pickedSlug = picked?.instance_name || ''
          snowflakePayload.query_filter.product_instances = [pickedSlug, ALL_SOURCES_SENTINEL]

          /* Re-resolve coverage with the A/B swap (sentinel may be in Source A). */
          const ctaCoverage = getCompareToAllCoverage(filters, fs === ALL_SOURCES_SENTINEL)
          if (ctaCoverage) {
            snowflakePayload.query_filter.coverage_comparison = ctaCoverage
          } else {
            delete snowflakePayload.query_filter.coverage_comparison
          }

          const ctaRes = await axios.post(
            '/api/v4/files/asset_coverage/export/compare-to-all/records',
            snowflakePayload
          )

          if (ctaRes.status.toString().includes('20')) {
            const fileName = `onyxia_asset_coverage_compare_to_all_${pickedSlug}_${dayjs().format('YYYYMMDD')}.csv`
            downloadFile(ctaRes.data, fileName)
          }
          return
        }

        const exportInstance1 = instances.find((n) => n.id === integrationIds[0])
        const exportInstance2 = instances.find((n) => n.id === integrationIds[1])
        const isV2 = Boolean(exportInstance1?.is_v2 && exportInstance2?.is_v2)

        let res
        if (isV2) {
          res = await axios.post('/api/v4/files/asset_coverage/download/records_csv', snowflakePayload)
        } else {
          res = await axios.post('/api/v3/aca/comparison/csv', payload)
        }

        if (res.status.toString().includes('20')) {
          const fileName = getDownloadCsvProps(selectedInstances)

          downloadFile(res.data, fileName)
        }
      } catch (err) {
        console.error(err)
      } finally {
        setExporting(false)
      }
    },
    [selectedOperatingSystem, selectedInstances, tableFilters, selectedSeenWithin, instances]
  )

  const filterAnalysisData = async (filters: boolean[], os: OperatingSystem) => {
    if (!allAnalysisData || !firstSource || !secondSource) {
      setLoadingAnalysisData(false)
      return []
    }

    setLoadingAnalysisData(true)

    const coverageFilterIndex = filters.findIndex((c) => c)

    const payload: AcaComparisonPayload = {
      operating_system: selectedOperatingSystem.toLowerCase() as AcaOperatingSystem,
      integration_ids: [firstSource, secondSource],
      last_seen: selectedSeenWithin.toLowerCase() as AcaLastSeen,
      page_number: 1,
      sort_by: 'last_seen'
    }
    if (coverageFilterIndex > -1) {
      payload.coverage =
        coverageFilterIndex === 0
          ? AcaCoverageFilter.FirstNotSecond
          : coverageFilterIndex === 1
          ? AcaCoverageFilter.SecondNotFirst
          : coverageFilterIndex === 2
          ? AcaCoverageFilter.Intersection
          : AcaCoverageFilter.SymmetricDifference
    }
    if (os) {
      payload.operating_system = os.toLowerCase() as AcaOperatingSystem
    }

    try {
      const res = await axios.post('/api/v3/aca/comparison', payload)

      if (res.status.toString().includes('20')) {
        setAnalysisData(res.data.data)
        setAllAnalysisData(res.data.data)
        setAnalyzedCompareToAll(false)
      }
    } catch (e) {
      console.error(e)
    } finally {
      setLoadingAnalysisData(false)
    }
  }

  useEffect(() => {
    fetchAll().catch((e) => e)
  }, [])

  return {
    isCompareToAll,
    analyzedCompareToAll,
    analyzedTrendMode,
    fetchTotalAssets,
    totalComparableAssets,
    exporting,
    setActiveSeenWithin,
    handleExport,
    activeSeenWithin,
    selectedOsTrends,
    setSelectedOsTrends,
    selectedInstances,
    initialLoad,
    sourceChanged,
    pagination,
    setSourceChanged,
    setPagination,
    tableFilters,
    setTableFilters,
    firstSource,
    secondSource,
    setFirstSource,
    setSecondSource,
    handleChangeSource,
    selectedOperatingSystem,
    setSelectedOperatingSystem,
    selectedSeenWithin,
    setSelectedSeenWithin,
    selectedTimePeriod,
    setSelectedTimePeriod,
    fetchTrendData,
    chartData,
    setChartData,
    chartLoading,
    setChartLoading,
    instances,
    supportedIntegrations,
    optionsLoading,
    analysisData,
    fetchAnalysisData,
    filterAnalysisData,
    loadingAnalysisData,
    setLoadingAnalysisData,
    firstInstanceAssets,
    secondInstanceAssets,
    numOfActiveAssets,
    lastActiveSeenWithin,
    setLastActiveSeenWithin
  }
}

export default useCoverageAnalyzerData
